import React from 'react';
import { useTranslation } from 'react-i18next';
import { pickProfileLocalPreviewUrl } from '../utils/profileLocalPreviewBlob';
import { resolveSignupSignatureSrc } from '../utils/resolvePreviewFileSrc';

/** layoutConfig 루트에 title 이 없을 때 미리보기·섹션 제목 기본값 */
export const DEFAULT_DOCUMENT_FORM_TITLE = 'PoC 연구 수행용 수요기술 요구사항';
const DEFAULT_DOCUMENT_SUBTITLE = '(기업·기관 제출용)';

/** 등록/상세 화면 섹션 제목·미리보기 상단 제목 공통 */
export function resolveDocumentFormTitle(structureJson, translate) {
  const raw = structureJson?.title;
  const fromLayout = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  const localizedDefault =
    typeof translate === 'function'
      ? translate('projects.register.documentFormTitle')
      : DEFAULT_DOCUMENT_FORM_TITLE;
  if (!fromLayout || fromLayout === DEFAULT_DOCUMENT_FORM_TITLE) {
    return localizedDefault;
  }
  return fromLayout;
}

/** subtitle 키가 있고 빈 문자열이면 부제 비표시(null). 키가 없으면 기존 기본 부제. */
function resolvePreviewSubtitle(structureJson) {
  if (!structureJson || !Object.prototype.hasOwnProperty.call(structureJson, 'subtitle')) {
    return DEFAULT_DOCUMENT_SUBTITLE;
  }
  const s = structureJson.subtitle;
  if (s == null || String(s).trim() === '') return null;
  return String(s).trim();
}

const val = (data, key) => {
  const v = data?.[key];
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? '■' : '□';
  return String(v);
};

/** FILE 타입(첨부) value는 JSON 배열 [{ name, size }] 문자열 → 파일명 목록으로 표시 */
const formatAttachmentDisplay = (v) => {
  if (v == null) return '';
  try {
    const list = JSON.parse(v);
    if (Array.isArray(list) && list.length) return list.map((x) => x?.name).filter(Boolean).join(', ');
  } catch { /* ignore */ }
  return String(v);
};

const normalizeDateDisplay = (raw) => {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{13}$/.test(s)) {
    const d = new Date(Number(s));
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return s;
};

const cellVal = (row, item, fileDisplayByKey) => {
  const v = row?.[item?.key];
  if (v == null) return '';
  if (item?.type === 'BOOLEAN' || typeof v === 'boolean') return v ? '■' : '□';
  // fileDisplayByKey에 해당 키가 있으면 cetus에서 조회한 파일명 표시 (API에서 type 미반환 시 대비)
  if (fileDisplayByKey && item?.key && Object.prototype.hasOwnProperty.call(fileDisplayByKey, item.key)) {
    return fileDisplayByKey[item.key];
  }
  const isFile = item?.type === 'FILE' || item?.dataType === 'FILE_TYPE';
  if (isFile) return formatAttachmentDisplay(v);
  const dt = String(item?.dataType || item?.type || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  if (dt === 'DATE_TYPE' || dt === 'DATE') {
    return normalizeDateDisplay(v);
  }
  if (dt === 'ENUM_TYPE' || dt === 'ENUM') {
    const opts = item?.options || item?.enumOptions || [];
    const found = opts.find((o) => String(o?.value ?? o?.key ?? '') === String(v));
    if (found?.label != null) return String(found.label);
  }
  return String(v);
};

/** 구분 열(poc-cat)에 넣을 그룹 제목이 있는지 — 없으면 라벨·값만 5열 맞춤 */
const hasCategoryTitle = (block) => block?.title != null && String(block.title).trim() !== '';

/**
 * ROW 미리보기 한 줄: 라벨이 필드당 1칸일 때 값 영역 colSpan을 균등 분배.
 * 마지막 값에만 남는 폭을 몰면 소속·성별·졸업연도 셀만 비정상적으로 넓어짐.
 */
const valueColSpansForPreviewRow = (dataSlotCount, fieldCount) => {
  const n = fieldCount;
  if (n < 1) return [];
  const valueSum = dataSlotCount - n;
  if (valueSum < 1) return Array(n).fill(1);
  const base = Math.floor(valueSum / n);
  const rem = valueSum % n;
  return Array.from({ length: n }, (_, j) => Math.max(1, base + (j < rem ? 1 : 0)));
};

/** TEXTAREA/에디터 값은 표 셀에서 줄바꿈 유지 */
const cellStyleForPreviewField = (item) => {
  const d = String(item?.displayType || item?.display_type || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  if (d === 'TEXTAREA' || d === 'EDITOR' || d === 'RICH_TEXT') {
    return {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      verticalAlign: 'top',
      minHeight: '3.5rem',
    };
  }
  return undefined;
};

/** 동의 줄 — 라벨 + 선택값 텍스트만 (■/□ 없음) */
function consentValuePlain(row, item) {
  const v = row?.[item?.key];
  if (v == null || v === '') return '-';
  if (item?.type === 'BOOLEAN' || typeof v === 'boolean') {
    if (typeof v === 'boolean') return v ? '동의' : '미동의';
    if (v === true || v === 1) return '동의';
    if (v === false || v === 0) return '미동의';
    const su = String(v).trim().toUpperCase();
    if (su === 'N' || su === 'NO' || su === 'FALSE') return '미동의';
    if (su === 'Y' || su === 'YES' || su === 'TRUE') return '동의';
    return String(v).trim() === '동의' ? '동의' : String(v).trim() === '미동의' ? '미동의' : String(v);
  }
  const dt = String(item?.dataType || item?.type || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  if (dt === 'ENUM_TYPE' || dt === 'ENUM') {
    const opts = item?.options || item.enumOptions || [];
    const found = opts.find((o) => String(o?.value ?? o?.key ?? '') === String(v));
    if (found?.label != null) return String(found.label);
  }
  const su = String(v).trim().toUpperCase();
  if (su === 'Y' || su === 'YES' || su === 'TRUE') return '동의';
  if (su === 'N' || su === 'NO' || su === 'FALSE') return '미동의';
  if (String(v).trim() === '동의' || String(v).trim() === '미동의') return String(v).trim();
  return String(v);
}

function consentLinePlain(row, item, labelFallback) {
  const label = item?.label || labelFallback || '';
  return `${label}    ${consentValuePlain(row, item)}`;
}

function consentLegacyPlain(data, label, key) {
  const v = data?.[key];
  if (v == null || v === '') return `${label}    -`;
  if (v === true || v === 1) return `${label}    동의`;
  if (v === false || v === 0) return `${label}    미동의`;
  const su = String(v).trim().toUpperCase();
  if (su === 'Y' || su === 'YES' || su === 'TRUE') return `${label}    동의`;
  if (su === 'N' || su === 'NO' || su === 'FALSE') return `${label}    미동의`;
  const t = String(v).trim();
  if (t === '동의' || t === '미동의') return `${label}    ${t}`;
  return `${label}    ${t}`;
}

/** layoutConfig label 이 없을 때만 구 양식 키용 (레이아웃 label 이 항상 우선) */
const CONSENT_LABEL_FALLBACK_BY_KEY = {
  privacy_agreement_1: '개인정보의 제공 및 활용 동의',
  privacy_agreement_2: '고유식별정보의 제공 및 활용 동의',
  privacy_agreement_3: '개인정보의 제3자 제공동의',
};

function isConsentYnFieldDef(it) {
  if (!it?.key) return false;
  if (it.type === 'BOOLEAN') return true;
  const dt = String(it.dataType || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  if (dt === 'ENUM_TYPE' || dt === 'ENUM') {
    const opts = it.options || it.enumOptions || [];
    const vals = opts.map((o) => String(o?.value ?? o?.key ?? '').toUpperCase());
    return vals.includes('Y') && vals.includes('N');
  }
  return false;
}

const formatKoreanDate = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}. ${m[2]}. ${m[3]}.`;
  return s;
};

/** 미리보기 푸터 작성일 — 항상 로컬 기준 오늘 */
const formatKoreanToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return formatKoreanDate(`${y}-${m}-${day}`);
};

const mergeFirstRows = (layoutRows) => {
  const merged = {};
  Object.keys(layoutRows || {}).forEach((k) => {
    const idx = Number(k);
    const rows = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
    if (!rows.length) return;
    const row0 = rows[0] || {};
    Object.keys(row0).forEach((key) => {
      merged[key] = row0[key];
    });
  });
  return merged;
};

const renderCatTitle = (title) => {
  const t = String(title || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t === '수요기술 개요') return <>수요기술<br />개요</>;
  if (t === '문제 정의 및 필요성') return <>문제 정의<br />및 필요성</>;
  if (t === '구체적 요구사항') return <>구체적<br />요구사항</>;
  if (t === '협력 및 지원 가능 범위') return <>협력 및<br />지원 가능<br />범위</>;
  if (t === '기대 성과 및 활용 계획') return <>기대 성과<br />및 활용<br />계획</>;
  return t;
};

// const itemByKey = (items, key) => (items || []).find((it) => String(it?.key) === String(key));

const isArrayFieldDef = (n) =>
  n &&
  (n.type === 'ARRAY' ||
    String(n.dataType || '')
      .toUpperCase()
      .replace(/[\s-]/g, '_') === 'ARRAY');

/** 섹션 fields 가 전부 ARRAY 이면 문서형(왼쪽 구분 + 소제목 열 + 표) */
const isArrayOnlyBlock = (block) => {
  const fields = block?.fields || block?.items || [];
  return fields.length > 0 && fields.every((f) => isArrayFieldDef(f) && f.key);
};

const isStructuralNode = (n) => n && ['ROW', 'SECTION', 'GROUP'].includes(n.type);

const isScalarFieldNode = (n) =>
  n && n.key && !isStructuralNode(n) && !isArrayFieldDef(n);

/** IMAGE 계열 스칼라 필드(type / dataType 기준) */
const isImageTypeScalarField = (n) => {
  if (!isScalarFieldNode(n)) return false;
  const t = String(n.type || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  const dt = String(n.dataType || '')
    .toUpperCase()
    .replace(/[\s-]/g, '_');
  return t === 'IMAGE' || dt === 'IMAGE_TYPE' || dt === 'IMAGE';
};

/** 외부 테이블 속성 등으로 INT_TYPE 등에 파일 UID만 올 때 — 키로만 인식 (VIEW 메타와 무관) */
const FILE_UID_SIDEBAR_KEYS_NORMALIZED = new Set([
  'profileuid',
  'profilephoto',
  'photourl',
]);

const normalizeFieldKeyForFileUidSidebar = (key) => {
  if (!key || typeof key !== 'string') return '';
  return key.replace(/[\s-]+/g, '_').replace(/_+/g, '').toLowerCase();
};

const isHardcodedFileUidSidebarScalarField = (n) => {
  if (!isScalarFieldNode(n)) return false;
  const nk = normalizeFieldKeyForFileUidSidebar(n.key);
  return nk !== '' && FILE_UID_SIDEBAR_KEYS_NORMALIZED.has(nk);
};

/** 사이드바 왼쪽 열로 쓸 선행 스칼라: IMAGE 계열 또는 파일 UID 하드코딩 키 */
const isImageSectionSidebarLeadingField = (a) =>
  isImageTypeScalarField(a) || isHardcodedFileUidSidebarScalarField(a);

/** 선행 필드 직후 SECTION → 왼쪽 열(이미지) + 오른쪽 SECTION 그리드 (VIEW 레이아웃 패턴) */
const isImageScalarSectionPair = (a, b) =>
  isImageSectionSidebarLeadingField(a) &&
  b?.type === 'SECTION' &&
  Array.isArray(b.fields) &&
  b.fields.length > 0;

/**
 * fields 순서대로 미리보기 라인 생성.
 * - SECTION(또는 vertical 루트): 자식마다 한 줄(ROW 는 내부에서만 2열 짝). 형제 스칼라끼리 짝짓지 않음 → user_tel / user_email 각각 full 행.
 * - ROW 내부: 연속 스칼라 전부 한 표 행(라벨-값 반복). 3개 이상이면 한 줄에 N쌍(미리보기 표는 7열까지 사용).
 * - ROW 내부: [스칼라 | SECTION(스칼라만)] → row_leader_stacked_section 한 블록(선행 필드 + 오른쪽 세로 스칼라 묶음).
 * allowImageSectionSidebar false: 중첩에서 (IMAGE/파일UID키)+SECTION 을 image_section_sidebar 로 묶지 않음.
 * rootMode: 최상위 fields 가 블록 type ROW 면 'row', SECTION 등이면 'vertical'.
 */
const buildPreviewLinesFromItems = (items, allowImageSectionSidebar = true, rootMode = 'vertical') => {
  const lines = [];

  const walkVertical = (fields) => {
    if (!Array.isArray(fields)) return;
    let i = 0;
    while (i < fields.length) {
      const a = fields[i];
      const b = fields[i + 1];
      if (!a) {
        i++;
        continue;
      }

      if (a.type === 'ROW' && a.fields) {
        walkRow(a.fields);
        i++;
        continue;
      }
      if (a.type === 'SECTION' && a.fields) {
        walkVertical(a.fields);
        i++;
        continue;
      }
      if (a.type === 'GROUP' && a.fields) {
        walkVertical(a.fields);
        i++;
        continue;
      }
      if (isArrayFieldDef(a) && a.key) {
        lines.push({ kind: 'array', field: a });
        i++;
        continue;
      }
      if (isScalarFieldNode(a)) {
        if (allowImageSectionSidebar && isImageScalarSectionPair(a, b)) {
          lines.push({ kind: 'image_section_sidebar', imageField: a, section: b });
          i += 2;
          continue;
        }
        lines.push({ kind: 'full', a });
        i++;
        continue;
      }

      i++;
    }
  };

  const walkRow = (fields) => {
    if (!Array.isArray(fields)) return;
    let i = 0;
    while (i < fields.length) {
      const a = fields[i];
      const b = fields[i + 1];
      if (!a) {
        i++;
        continue;
      }

      if (a.type === 'ROW' && a.fields) {
        walkRow(a.fields);
        i++;
        continue;
      }
      if (a.type === 'SECTION' && a.fields) {
        walkVertical(a.fields);
        i++;
        continue;
      }
      if (a.type === 'GROUP' && a.fields) {
        walkVertical(a.fields);
        i++;
        continue;
      }
      if (isArrayFieldDef(a) && a.key) {
        lines.push({ kind: 'array', field: a });
        i++;
        continue;
      }
      if (isScalarFieldNode(a)) {
        if (allowImageSectionSidebar && isImageScalarSectionPair(a, b)) {
          lines.push({ kind: 'image_section_sidebar', imageField: a, section: b });
          i += 2;
          continue;
        }
        /* ROW: 선행 스칼라 + SECTION(스칼라만) — 미리보기에서 한 행 블록으로 유지 (DocumentFormEditArea ROW와 동일 패턴) */
        if (
          b?.type === 'SECTION' &&
          Array.isArray(b.fields) &&
          b.fields.length > 0 &&
          b.fields.every((f) => f && isScalarFieldNode(f) && !isArrayFieldDef(f))
        ) {
          lines.push({ kind: 'row_leader_stacked_section', leftField: a, section: b });
          i += 2;
          continue;
        }
        const cols = [];
        while (i < fields.length && isScalarFieldNode(fields[i])) {
          cols.push(fields[i]);
          i += 1;
        }
        if (cols.length >= 2) {
          lines.push({ kind: 'row', cols });
        } else if (cols.length === 1) {
          lines.push({ kind: 'full', a: cols[0] });
        }
        continue;
      }

      i++;
    }
  };

  if (rootMode === 'row') {
    walkRow(items);
  } else {
    walkVertical(items);
  }

  return { lines, extras: [] };
};

/**
 * DocumentFormEditArea FormFieldCell IMAGE 분기와 동일: data:/http:/절대·상대 경로/blob → 그대로, 그 외 파일 UID → /api/glow/files/…
 */
const resolveFormFieldImageSrc = (raw) => {
  if (raw == null || raw === '') return '';
  let v = raw;
  if (typeof v === 'object' && v !== null) {
    v =
      v.file_uid ??
      v.fileUid ??
      v.file_id ??
      v.fileId ??
      v.uid ??
      '';
  }
  const str = String(v).trim();
  if (!str) return '';
  if (str.startsWith('data:') || str.startsWith('http') || str.startsWith('/')) return str;
  if (str.startsWith('blob:')) return str;
  if (isNaN(Number(str))) return `/api/glow/files/view?fileId=${encodeURIComponent(str)}`;
  return `/api/glow/files/viewByUid/${str}`;
};

const resolveImageFieldPreviewSrc = (row, imageField, localImageSrcByKey) => {
  const blobFirst = pickProfileLocalPreviewUrl(localImageSrcByKey || {}, imageField?.key);
  if (blobFirst) return blobFirst;

  const key = imageField?.key;
  if (!key || row == null) return '';
  return resolveFormFieldImageSrc(row[key]);
};

export default function PocTechRequirementsPreview({
  data,
  signatureDataUrl,
  previewSections,
  structureJson,
  dataJson,
  fileDisplayByKey,
  /** 입력폼에서만 있는 blob: URL 등 — 필드 key → src (회원가입 프로필 미리보기와 동일) */
  localImageSrcByKey = {},
  /**
   * 회원가입(개인) 문서 미리보기 전용 푸터(오늘 작성일·G-RISE 블록·user_nm 우선 작성자).
   * PoC 수요기술·공고 등 일반 문서폼에서는 false(기본) — DIVIDER 있을 때만 기존 스타일 푸터.
   */
  showSignupDocumentFooter = false,
}) {
  const { t } = useTranslation();
  const writtenDateLabel = t('popup.previewPopup.writtenDate');
  const authorLabel = t('popup.previewPopup.author');
  const signatureLabel = t('popup.previewPopup.signature');
  const signatureAlt = t('popup.previewPopup.signatureAlt');
  const isDynamic = !!(structureJson?.layout && dataJson?.layoutRows);
  const previewTitle = resolveDocumentFormTitle(structureJson, t);
  const previewSubtitle = resolvePreviewSubtitle(structureJson);

  if (isDynamic) {
    const layout = Array.isArray(structureJson?.layout) ? structureJson.layout : [];
    const layoutRows = dataJson?.layoutRows || {};
    const flat = mergeFirstRows(layoutRows);

    // DIVIDER(동의/작성일 등) 키들 추출
    const dividerIdx = layout.findIndex(b => b?.type === 'DIVIDER');
    const divider = dividerIdx >= 0 ? layout[dividerIdx] : null;
    const dividerItems = divider?.type === 'DIVIDER' ? (divider.items || []) : [];
    const dividerRow0 = Array.isArray(layoutRows?.[dividerIdx]) ? (layoutRows[dividerIdx]?.[0] || {}) : {};
    const writtenDateKey = dividerItems.find(it => it?.key === 'written_date')?.key;
    const dividerRows = Array.isArray(layoutRows?.[dividerIdx]) ? layoutRows[dividerIdx] : [];
    /** items 없는 DIVIDER(구분선만)도 row [{}]가 생기므로, 실제 divider 필드가 있을 때만 표 하단 동의 행·푸터 표시 */
    const showDivider =
      dividerIdx >= 0 && dividerRows.length > 0 && Array.isArray(dividerItems) && dividerItems.length > 0;

    const authorCompany = String(flat?.company_name || '').trim();
    const authorName = String(flat?.contact_person || '').trim();
    const authorText = [authorCompany, authorName].filter(Boolean).join(' ');
    const authorSep = authorCompany && authorName ? '\u00A0\u00A0' : ' ';
    const authorPretty = authorCompany && authorName ? `${authorCompany}${authorSep}${authorName}` : (authorText || '');
    const authorFooterSignup =
      String(flat?.user_nm || '').trim() ||
      authorPretty ||
      String(flat?.writer_name || '').trim() ||
      String(flat?.full_name || '').trim() ||
      '-';
    const authorFooterDefault =
      authorPretty ||
      String(flat?.writer_name || '').trim() ||
      String(flat?.full_name || '').trim() ||
      '-';
    const authorFooterDisplay = showSignupDocumentFooter ? authorFooterSignup : authorFooterDefault;

    const resolvedSignatureSrc =
      (signatureDataUrl != null && String(signatureDataUrl).trim() !== ''
        ? String(signatureDataUrl).trim()
        : '') ||
      resolveSignupSignatureSrc(flat, dividerRow0, dataJson) ||
      '';

    const footerDateRaw =
      showDivider && writtenDateKey
        ? dividerRow0?.[writtenDateKey] || flat?.written_date || flat?.join_date || ''
        : flat?.written_date || flat?.join_date || '';

    const footerExtraAfterDate =
      (showDivider
        ? [dividerRow0?.company_name_footer, dividerRow0?.contact_person_footer]
        : [flat?.company_name_footer, flat?.contact_person_footer]
      )
        .filter(Boolean)
        .join(' / ');

    const tableSegments = [];

    const appendImageSectionSidebarRows = (segRows, fr, row, idx, rIdx, frIdx) => {
      const { imageField, section } = fr;
      const sublines = buildPreviewLinesFromItems(section.fields, false, 'vertical').lines;
      const lineCount = Math.max(
        1,
        sublines.reduce((acc, sub) => {
          if (sub.kind === 'row_leader_stacked_section') {
            return acc + Math.max(1, sub.section?.fields?.length || 0);
          }
          return acc + 1;
        }, 0)
      );
      const imageSrc = resolveImageFieldPreviewSrc(row, imageField, localImageSrcByKey);
      const renderSidebarPhoto = () =>
        imageSrc ? (
          <img className="personal-photo-img" src={imageSrc} alt={imageField?.label || ''} />
        ) : (
          <div className="personal-photo-placeholder" aria-hidden="true" />
        );

      if (sublines.length === 0) {
        segRows.push(
          <tr key={`poc-${idx}-r${rIdx}-ps${frIdx}-empty`}>
            <td className="poc-cat personal-photo-cell" rowSpan={1}>
              {renderSidebarPhoto()}
            </td>
            <td className="poc-value" colSpan={6}>
              -
            </td>
          </tr>
        );
        return;
      }

      let globalLineIndex = 0;
      sublines.forEach((sub, subIdx) => {
        const key = `poc-${idx}-r${rIdx}-ps${frIdx}-l${subIdx}`;
        if (sub.kind === 'row_leader_stacked_section') {
          const rights = sub.section?.fields || [];
          const rs = Math.max(1, rights.length);
          rights.forEach((rf, rj) => {
            const showPhoto = globalLineIndex === 0;
            const rowKey = `${key}-v${rj}`;
            if (rj === 0) {
              segRows.push(
                <tr key={rowKey}>
                  {showPhoto ? (
                    <td className="poc-cat personal-photo-cell" rowSpan={lineCount}>
                      {renderSidebarPhoto()}
                    </td>
                  ) : null}
                  <td className="poc-label" rowSpan={rs}>
                    {sub.leftField?.label}
                  </td>
                  <td
                    className="poc-value"
                    rowSpan={rs}
                    style={cellStyleForPreviewField(sub.leftField)}
                  >
                    {cellVal(row, sub.leftField, fileDisplayByKey) || '-'}
                  </td>
                  <td className="poc-label">{rf?.label}</td>
                  <td
                    className="poc-value"
                    colSpan={3}
                    style={cellStyleForPreviewField(rf)}
                  >
                    {cellVal(row, rf, fileDisplayByKey) || '-'}
                  </td>
                </tr>
              );
            } else {
              segRows.push(
                <tr key={rowKey}>
                  <td className="poc-label">{rf?.label}</td>
                  <td
                    className="poc-value"
                    colSpan={3}
                    style={cellStyleForPreviewField(rf)}
                  >
                    {cellVal(row, rf, fileDisplayByKey) || '-'}
                  </td>
                </tr>
              );
            }
            globalLineIndex += 1;
          });
          return;
        }

        const showPhoto = globalLineIndex === 0;
        globalLineIndex += 1;
        if (sub.kind === 'row') {
          const cols = Array.isArray(sub.cols) ? sub.cols : [];
          const dataSlotCount = 6; /* 사진 열 제외 우측 6칸 (7열 personal-table) */
          const n = cols.length;
          const vSpans = valueColSpansForPreviewRow(dataSlotCount, n);
          segRows.push(
            <tr key={key}>
              {showPhoto ? (
                <td className="poc-cat personal-photo-cell" rowSpan={lineCount}>
                  {renderSidebarPhoto()}
                </td>
              ) : null}
              {cols.map((field, j) => (
                <React.Fragment key={`${key}-c${j}`}>
                  <td className="poc-label">{field?.label}</td>
                  <td
                    className="poc-value"
                    colSpan={vSpans[j] ?? 1}
                    style={cellStyleForPreviewField(field)}
                  >
                    {cellVal(row, field, fileDisplayByKey) || '-'}
                  </td>
                </React.Fragment>
              ))}
            </tr>
          );
        } else if (sub.kind === 'full') {
          const f = sub.a;
          segRows.push(
            <tr key={key}>
              {showPhoto ? (
                <td className="poc-cat personal-photo-cell" rowSpan={lineCount}>
                  {renderSidebarPhoto()}
                </td>
              ) : null}
              <td className="poc-label">{f?.label}</td>
              <td className="poc-value" colSpan={5} style={cellStyleForPreviewField(f)}>
                {cellVal(row, f, fileDisplayByKey) || '-'}
              </td>
            </tr>
          );
        } else if (sub.kind === 'array') {
          const af = sub.field;
          const cols = af.fields || af.columns || [];
          const dataRows = Array.isArray(row[af.key]) ? row[af.key] : [{}];
          const arrayMatrix = isArrayOnlyBlock(section);
          const nested = cols.length > 0 ? (
            <table className="poc-array-nested-table mb-0 bg-white">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.key} className="small">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(dataRows.length ? dataRows : [{}]).map((dr, di) => (
                  <tr key={`${key}-ar-${di}`}>
                    {cols.map((c) => (
                      <td key={c.key} className="small" style={cellStyleForPreviewField(c)}>
                        {cellVal(dr, c, fileDisplayByKey) || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span className="text-muted small">—</span>
          );
          segRows.push(
            <tr key={key}>
              {showPhoto ? (
                <td className="poc-cat personal-photo-cell" rowSpan={lineCount}>
                  {renderSidebarPhoto()}
                </td>
              ) : null}
              {arrayMatrix ? (
                <>
                  <td className="poc-label poc-array-sublabel">{af.label}</td>
                  <td className="poc-value p-2 poc-array-matrix-value" colSpan={5}>
                    {nested}
                  </td>
                </>
              ) : (
                <td className="poc-value p-2" colSpan={6}>
                  {af.label ? (
                    <div className="fw-bold text-muted small mb-2">{af.label}</div>
                  ) : null}
                  {nested}
                </td>
              )}
            </tr>
          );
        }
      });
    };

    const appendStandardFrRow = (
      segRows,
      fr,
      row,
      block,
      idx,
      rIdx,
      frIdx,
      catRowSpan,
      isFirstStdWithCat,
      tableColCount
    ) => {
      const showCat = hasCategoryTitle(block);
      if (fr.kind === 'row') {
        const cols = Array.isArray(fr.cols) ? fr.cols : [];
        if (cols.length < 2) return;
        const n = cols.length;
        const dataSlotCount = showCat ? tableColCount - 1 : tableColCount;
        const vSpans = valueColSpansForPreviewRow(dataSlotCount, n);
        segRows.push(
          <tr key={`poc-sec-${idx}-r${rIdx}-f${frIdx}`}>
            {isFirstStdWithCat && showCat && (
              <td className="poc-cat" rowSpan={catRowSpan}>
                {renderCatTitle(block?.title)}
              </td>
            )}
            {cols.map((field, j) => (
              <React.Fragment key={`poc-sec-${idx}-r${rIdx}-f${frIdx}-c${j}`}>
                <td className="poc-label">{field?.label}</td>
                <td
                  className="poc-value"
                  colSpan={vSpans[j] ?? 1}
                  style={cellStyleForPreviewField(field)}
                >
                  {cellVal(row, field, fileDisplayByKey) || '-'}
                </td>
              </React.Fragment>
            ))}
          </tr>
        );
      } else if (fr.kind === 'array') {
        const af = fr.field;
        const cols = af.fields || af.columns || [];
        const dataRows = Array.isArray(row[af.key]) ? row[af.key] : [{}];
        const arrayMatrix = isArrayOnlyBlock(block);
        const nested = cols.length > 0 ? (
          <table className="poc-array-nested-table mb-0 bg-white">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className="small">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(dataRows.length ? dataRows : [{}]).map((dr, di) => (
                <tr key={`poc-sec-${idx}-r${rIdx}-arr-${frIdx}-${di}`}>
                  {cols.map((c) => (
                    <td key={c.key} className="small" style={cellStyleForPreviewField(c)}>
                      {cellVal(dr, c, fileDisplayByKey) || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <span className="text-muted small">—</span>
        );
        const valueColSpan = arrayMatrix
          ? showCat
            ? tableColCount - 2
            : tableColCount - 1
          : showCat
            ? tableColCount - 2
            : tableColCount - 1;
        segRows.push(
          <tr key={`poc-sec-${idx}-r${rIdx}-arr-${frIdx}`}>
            {isFirstStdWithCat && showCat && (
              <td className="poc-cat" rowSpan={catRowSpan}>
                {renderCatTitle(block?.title)}
              </td>
            )}
            {arrayMatrix ? (
              <>
                <td className="poc-label poc-array-sublabel">{af.label}</td>
                <td className="poc-value p-2 poc-array-matrix-value" colSpan={valueColSpan}>
                  {nested}
                </td>
              </>
            ) : (
              <>
                <td className="poc-label">{af.label || af.key}</td>
                <td className="poc-value p-2" colSpan={valueColSpan}>
                {nested}
                </td>
              </>
            )}
          </tr>
        );
      } else if (fr.kind === 'full') {
        if (!showCat) {
          segRows.push(
            <tr key={`poc-sec-${idx}-r${rIdx}-f${frIdx}`}>
              <td className="poc-label">{fr.a?.label}</td>
              <td className="poc-value" colSpan={tableColCount - 1} style={cellStyleForPreviewField(fr.a)}>
                {cellVal(row, fr.a, fileDisplayByKey)}
              </td>
            </tr>
          );
        } else {
          segRows.push(
            <tr key={`poc-sec-${idx}-r${rIdx}-f${frIdx}`}>
              {isFirstStdWithCat && (
                <td className="poc-cat" rowSpan={catRowSpan}>
                  {renderCatTitle(block?.title)}
                </td>
              )}
              <td className="poc-label">{fr.a?.label}</td>
              <td className="poc-value" colSpan={tableColCount - 2} style={cellStyleForPreviewField(fr.a)}>
                {cellVal(row, fr.a, fileDisplayByKey)}
              </td>
            </tr>
          );
        }
      }
    };

    /** DIVIDER 아래 블록: 문서 표(poc-table) 대신 라벨·값 일반 텍스트 */
    const renderPlainScalarValue = (row, field) => {
      if (isImageTypeScalarField(field)) {
        const src = resolveImageFieldPreviewSrc(row, field, localImageSrcByKey);
        return src ? (
          <img className="poc-plain-inline-img" src={src} alt="" />
        ) : (
          <span className="text-muted">—</span>
        );
      }
      const raw = cellVal(row, field, fileDisplayByKey);
      const style = cellStyleForPreviewField(field);
      return <span style={style}>{raw || '—'}</span>;
    };

    const renderPlainOneFr = (fr, row, keyPrefix) => {
      if (fr.kind === 'row') {
        const cols = Array.isArray(fr.cols) ? fr.cols : [];
        return (
          <div key={keyPrefix} className="poc-plain-field-row">
            {cols.map((field, j) => (
              <div key={field?.key || `c-${j}`} className="poc-plain-field">
                <span className="poc-plain-label">{field?.label}</span>
                <span className="poc-plain-value">{renderPlainScalarValue(row, field)}</span>
              </div>
            ))}
          </div>
        );
      }
      if (fr.kind === 'full') {
        return (
          <div key={keyPrefix} className="poc-plain-field">
            <span className="poc-plain-label">{fr.a?.label}</span>
            <span className="poc-plain-value">{renderPlainScalarValue(row, fr.a)}</span>
          </div>
        );
      }
      if (fr.kind === 'array') {
        const af = fr.field;
        const cols = af.fields || af.columns || [];
        const dataRows = Array.isArray(row[af.key]) ? row[af.key] : [{}];
        return (
          <div key={keyPrefix} className="poc-plain-array-block">
            {af.label ? <div className="poc-plain-array-heading">{af.label}</div> : null}
            {(dataRows.length ? dataRows : [{}]).map((dr, di) => (
              <div key={`${keyPrefix}-ar-${di}`} className="poc-plain-array-entry">
                <div className="poc-plain-array-entry-fields">
                  {cols.map((c) => (
                    <div key={c.key} className="poc-plain-field poc-plain-field-nested">
                      <span className="poc-plain-label">{c.label}</span>
                      <span className="poc-plain-value">
                        <span style={cellStyleForPreviewField(c)}>{cellVal(dr, c, fileDisplayByKey) || '—'}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      }
      if (fr.kind === 'row_leader_stacked_section') {
        const { leftField, section } = fr;
        const rights = section?.fields || [];
        return (
          <div key={keyPrefix} className="poc-plain-field-row poc-plain-row-leader-stack">
            <div className="poc-plain-field poc-plain-field--leader">
              <span className="poc-plain-label">{leftField?.label}</span>
              <span className="poc-plain-value">{renderPlainScalarValue(row, leftField)}</span>
            </div>
            <div className="poc-plain-row-leader-stack__stacked">
              {rights.map((rf) => (
                <div key={rf.key || rf.label} className="poc-plain-field poc-plain-field-nested">
                  <span className="poc-plain-label">{rf.label}</span>
                  <span className="poc-plain-value">{renderPlainScalarValue(row, rf)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      if (fr.kind === 'image_section_sidebar') {
        const { imageField, section } = fr;
        const sublines = buildPreviewLinesFromItems(section.fields, false, 'vertical').lines;
        const imageSrc = resolveImageFieldPreviewSrc(row, imageField, localImageSrcByKey);
        return (
          <div key={keyPrefix} className="poc-plain-image-sidebar">
            <div className="poc-plain-photo">
              {imageSrc ? (
                <img src={imageSrc} alt={imageField?.label || ''} className="poc-plain-inline-img" />
              ) : (
                <div className="personal-photo-placeholder" aria-hidden="true" />
              )}
            </div>
            <div className="poc-plain-sidebar-fields">
              {sublines.map((sub, si) => renderPlainOneFr(sub, row, `${keyPrefix}-sub-${si}`))}
            </div>
          </div>
        );
      }
      return null;
    };

    const plainPostDividerBlocks = [];
    if (dividerIdx >= 0) {
      (layout || []).forEach((block, idx) => {
        if (idx <= dividerIdx || block?.type === 'DIVIDER') return;
        const rows = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
        if (!rows.length) return;
        const items = block?.fields || block?.items || [];
        const rootMode = block.type === 'ROW' ? 'row' : 'vertical';
        const templateLines = buildPreviewLinesFromItems(items, true, rootMode).lines;
        const blockTitle = block?.title != null && String(block.title).trim() !== '' ? String(block.title).trim() : '';
        plainPostDividerBlocks.push(
          <div key={`poc-plain-block-${idx}`} className="poc-plain-block">
            {blockTitle ? <div className="poc-plain-block-title">{blockTitle}</div> : null}
            {rows.map((row, rIdx) => (
              <div key={`poc-plain-rs-${idx}-${rIdx}`} className="poc-plain-row-set">
                {templateLines.map((fr, frIdx) =>
                  renderPlainOneFr(fr, row, `poc-plain-${idx}-${rIdx}-${frIdx}`)
                )}
              </div>
            ))}
          </div>
        );
      });
    }

    let dynamicRestColCount = 5;
    (layout || []).forEach((block, idx) => {
      if (block?.type === 'DIVIDER') return;
      if (dividerIdx >= 0 && idx > dividerIdx) return;
      const rs = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
      if (!rs.length) return;
      const blockItems = block?.fields || block?.items || [];
      const rootMode = block.type === 'ROW' ? 'row' : 'vertical';
      const tl = buildPreviewLinesFromItems(blockItems, true, rootMode).lines;
      for (const l of tl) {
        if (l.kind === 'row' && Array.isArray(l.cols) && l.cols.length >= 3) {
          dynamicRestColCount = 7;
        }
      }
    });

    (layout || []).forEach((block, idx) => {
      if (block?.type === 'DIVIDER') return;
      if (dividerIdx >= 0 && idx > dividerIdx) return;

      const items = block?.fields || block?.items || [];
      const rows = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
      if (!rows.length) return;

      const rootMode = block.type === 'ROW' ? 'row' : 'vertical';
      const templateLines = buildPreviewLinesFromItems(items, true, rootMode).lines;
      const stdCountPerDataRow = templateLines.filter((l) => l.kind !== 'image_section_sidebar').length;
      const catRowSpan = stdCountPerDataRow * rows.length;

      let blockStdSeen = 0;
      let curSeg = null;

      const flushSeg = () => {
        if (curSeg && curSeg.rows.length) tableSegments.push(curSeg);
        curSeg = null;
      };

      rows.forEach((row, rIdx) => {
        templateLines.forEach((fr, frIdx) => {
          const wantSidebarLeaderTable = fr.kind === 'image_section_sidebar';
          const kind = wantSidebarLeaderTable ? 'sidebar_leader_table' : 'rest5';
          if (!curSeg || curSeg.kind !== kind) {
            flushSeg();
            curSeg = {
              kind,
              rows: [],
              colCount: kind === 'rest5' ? dynamicRestColCount : undefined,
            };
          }
          if (wantSidebarLeaderTable) {
            appendImageSectionSidebarRows(curSeg.rows, fr, row, idx, rIdx, frIdx);
          } else {
            const isFirstCat = hasCategoryTitle(block) && blockStdSeen === 0;
            appendStandardFrRow(
              curSeg.rows,
              fr,
              row,
              block,
              idx,
              rIdx,
              frIdx,
              catRowSpan,
              isFirstCat,
              curSeg.colCount ?? 5
            );
            blockStdSeen += 1;
          }
        });
      });
      flushSeg();
    });

    if (showDivider) {
      const consentItems = dividerItems.filter(isConsentYnFieldDef);
      if (consentItems.length > 0) {
        const dividerColSpan =
          tableSegments.length > 0
            ? tableSegments[tableSegments.length - 1]?.colCount ?? dynamicRestColCount
            : dynamicRestColCount;
        const divTr = (
          <tr key="poc-divider">
            <td colSpan={dividerColSpan} style={{ padding: 0 }}>
              <div className="poc-consent-lines">
                {(consentItems || []).map((it) => (
                  <div key={`poc-consent-${it.key}`} className="line">
                    {consentLinePlain(flat, it, CONSENT_LABEL_FALLBACK_BY_KEY[it.key])}
                  </div>
                ))}
              </div>
            </td>
          </tr>
        );
        const last = tableSegments[tableSegments.length - 1];
        if (last && last.kind === 'rest5') {
          last.rows.push(divTr);
        } else {
          tableSegments.push({ kind: 'rest5', rows: [divTr], colCount: dynamicRestColCount });
        }
      }
    }

    return (
      <div className="poc-preview-shell dfb-preview-shell">
        <div className="poc-preview-page dfb-preview-page">
          <div className="poc-preview">
            <div className="poc-title">{previewTitle}</div>
            <div className="poc-title-line"></div>
            {previewSubtitle != null ? <div className="poc-sub">{previewSubtitle}</div> : null}

            {tableSegments.map((seg, si) => (
              <table
                key={`poc-seg-${si}`}
                className={`poc-table${seg.kind === 'sidebar_leader_table' ? ' personal-table poc-table-profile-leader' : ''}${
                  si > 0 ? ' poc-table-stack-continue' : ''
                }`}
              >
                {seg.kind === 'sidebar_leader_table' ? (
                  <colgroup>
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                  </colgroup>
                ) : seg.colCount >= 7 ? (
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '25%' }} />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '27%' }} />
                  </colgroup>
                )}
                <tbody>{seg.rows}</tbody>
              </table>
            ))}

            {plainPostDividerBlocks.length > 0 ? (
              <div className="poc-plain-after-divider">{plainPostDividerBlocks}</div>
            ) : null}

            {/* 회원가입 미리보기 전용 푸터 — 상위에서 showSignupDocumentFooter 로만 켬 */}
            {showSignupDocumentFooter ? (
              <div className="poc-footer">
                <div className="poc-footer-date">
                  {writtenDateLabel} {formatKoreanToday()}
                  {footerExtraAfterDate ? <span className="ms-3">{footerExtraAfterDate}</span> : null}
                </div>

                <div className="poc-footer-row">
                  <div className="poc-logo">
                    <div className="mark">ⓘ</div>
                    <div className="txt">
                      <div className="ko">한국외국어대학교 G-RISE 사업단</div>
                      <div className="en">HUFS G-Regional Innovation System & Education Center</div>
                    </div>
                  </div>

                  <div className="poc-author">
                    <span>{authorLabel}: {authorFooterDisplay}</span>
                    <div className="poc-author-signature-overlap">
                      {resolvedSignatureSrc ? (
                        <img src={resolvedSignatureSrc} alt={signatureAlt} />
                      ) : null}
                      <span className="poc-author-signature-label">{signatureLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {!showDivider && resolvedSignatureSrc ? (
                  <div className="poc-footer">
                    <div className="poc-footer-row">
                      <div className="poc-author">
                        <div className="poc-author-signature-overlap">
                          <img src={resolvedSignatureSrc} alt={signatureAlt} />
                          <span className="poc-author-signature-label">{signatureAlt}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {showDivider ? (
                  <div className="poc-footer">
                    <div className="poc-footer-date">
                      {writtenDateLabel} {formatKoreanDate(footerDateRaw)}
                      {footerExtraAfterDate ? <span className="ms-3">{footerExtraAfterDate}</span> : null}
                    </div>

                    <div className="poc-footer-row">
                      <div className="poc-logo">
                        <div className="mark">ⓘ</div>
                        <div className="txt">
                          <div className="ko">한국외국어대학교 G-RISE 사업단</div>
                          <div className="en">HUFS G-Regional Innovation System & Education Center</div>
                        </div>
                      </div>

                      <div className="poc-author">
                        <span>{authorLabel}: {authorFooterDisplay}</span>
                        <div className="poc-author-signature-overlap">
                          {resolvedSignatureSrc ? (
                            <img src={resolvedSignatureSrc} alt={signatureAlt} />
                          ) : null}
                          <span className="poc-author-signature-label">{signatureLabel}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const hasRows = (previewSections, idx) => {
    const sec = (previewSections || []).find(s => Number(s?.idx) === Number(idx));
    return (sec?.rows?.length || 0) > 0;
  };
  const show0 = hasRows(previewSections, 0); // 기본정보
  const show1 = hasRows(previewSections, 1); // 수요기술개요
  const show2 = hasRows(previewSections, 2); // 문제정의/필요성
  const show3 = hasRows(previewSections, 3); // 구체적요구사항
  const show4 = hasRows(previewSections, 4); // 협력/지원
  const show5 = hasRows(previewSections, 5); // 기대성과
  const show6 = hasRows(previewSections, 6); // 기타사항
  const show7 = hasRows(previewSections, 7); // 동의/작성일/서명(divider)

  return (
    <div className="poc-preview-shell dfb-preview-shell">
      <div className="poc-preview-page dfb-preview-page">
        <div className="poc-preview">
          <div className="poc-title">{previewTitle}</div>
          <div className="poc-title-line"></div>
          {previewSubtitle != null ? <div className="poc-sub">{previewSubtitle}</div> : null}

          <table className="poc-table">
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '27%' }} />
            </colgroup>
            <tbody>
              {show0 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={6}>기본정보</td>
                    <td className="poc-label">기관명·기업명</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'company_name')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">소재지</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'location')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">부서</td>
                    <td className="poc-value">{val(data, 'department')}</td>
                    <td className="poc-label">담당자명</td>
                    <td className="poc-value">{val(data, 'contact_person')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">직위(직책)</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'position')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">연락처</td>
                    <td className="poc-value">{val(data, 'phone')}</td>
                    <td className="poc-label">이메일</td>
                    <td className="poc-value">{val(data, 'contact_email')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">홈페이지·공식채널</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'homepage')}</td>
                  </tr>
                </>
              )}

              {show1 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={3}>수요기술<br />개요</td>
                    <td className="poc-label">기술 분야</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'tech_field')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">세부 키워드</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'keywords')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">적용 산업·제품 영역</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'application_industry')}</td>
                  </tr>
                </>
              )}

              {show2 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={3}>문제 정의<br />및 필요성</td>
                    <td className="poc-label">현재 직면한 기술문제</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'current_tech_issue')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">문제 발생 배경 및 영향</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'issue_background')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">해결 필요성</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'solution_necessity')}</td>
                  </tr>
                </>
              )}

              {show3 && (
                <tr>
                  <td className="poc-cat">구체적<br />요구사항</td>
                  <td className="poc-label">기술 요구사항</td>
                  <td className="poc-value" colSpan={3}>{val(data, 'tech_requirements')}</td>
                </tr>
              )}

              {show4 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={2}>협력 및<br />지원 가능<br />범위</td>
                    <td className="poc-label">희망 협력 분야</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'cooperation_field')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">기업 제공 가능 자원</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'provided_resources')}</td>
                  </tr>
                </>
              )}

              {show5 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={2}>기대 성과<br />및 활용<br />계획</td>
                    <td className="poc-label">PoC 연구 성과의 활용 계획</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'poc_utilization_plan')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">성과 확산 방식</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'outcome_diffusion')}</td>
                  </tr>
                </>
              )}

              {show6 && (
                <>
                  <tr>
                    <td className="poc-cat" rowSpan={2}>기타 사항</td>
                    <td className="poc-label">기타 요청 사항</td>
                    <td className="poc-value" colSpan={3}>{val(data, 'other_requests')}</td>
                  </tr>
                  <tr>
                    <td className="poc-label">관련 문서 첨부</td>
                    <td className="poc-value" colSpan={3}>{formatAttachmentDisplay(data?.attachment)}</td>
                  </tr>
                </>
              )}

              {show7 && (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <div className="poc-consent-lines">
                      <div className="line">{consentLegacyPlain(data, '개인정보의 제공 및 활용 동의', 'privacy_agreement_1')}</div>
                      <div className="line">{consentLegacyPlain(data, '고유식별정보의 제공 및 활용 동의', 'privacy_agreement_2')}</div>
                      <div className="line">{consentLegacyPlain(data, '개인정보의 제3자 제공동의', 'privacy_agreement_3')}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {show7 && (
            <div className="poc-footer">
              <div className="poc-footer-date">
                {writtenDateLabel}{' '}
                {showSignupDocumentFooter
                  ? formatKoreanToday()
                  : formatKoreanDate(val(data, 'written_date'))}
                {(val(data, 'company_name_footer') || val(data, 'contact_person_footer')) && (
                  <span className="ms-3">
                    {[val(data, 'company_name_footer'), val(data, 'contact_person_footer')].filter(Boolean).join(' / ')}
                  </span>
                )}
              </div>

              <div className="poc-footer-row">
                <div className="poc-logo">
                  <div className="mark">ⓘ</div>
                  <div className="txt">
                    <div className="ko">한국외국어대학교 G-RISE 사업단</div>
                    <div className="en">HUFS G-Regional Innovation System & Education Center</div>
                  </div>
                </div>

                <div className="poc-author">
                  <span>
                    {authorLabel}:{' '}
                    {showSignupDocumentFooter
                      ? String(val(data, 'user_nm') || '').trim() || '-'
                      : '주식회사 메디코슨\u00A0\u00A0김 상 범'}
                  </span>
                  <div className="poc-author-signature-overlap">
                    {signatureDataUrl && <img src={signatureDataUrl} alt={signatureAlt} />}
                    <span className="poc-author-signature-label">{signatureLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

